import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams, TableQueryState } from '@/components/DataTable';
import type { PluginListItem } from '@kirin/api-client/contracts/plugins';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { trackEvent } from '@/lib/analytics';
import { PluginVersionsTable } from '@/components/shared/plugins/PluginVersionsTable';
import { PluginUserListModal } from '@/pages/organization/components/PluginUserListModal';
import { createPluginColumns } from './columns';
import { fetchOrgPlugins, fetchPluginFieldFilterOptions } from './api';
import { ORG_PLUGIN_TABLE_CONFIG } from './constants';

/**
 * Stable per-row identifier. The list aggregation groups on
 * `(pluginName, marketplace)`, so two rows can share a pluginName when the
 * marketplaces differ — the React/cache key has to include both.
 */
function pluginRowKey(plugin: {
  pluginName: string;
  marketplace: string | null;
}): string {
  return `${plugin.pluginName}@${plugin.marketplace ?? ''}`;
}

interface SelectedPluginUsers {
  pluginName: string;
  marketplace: string | null;
  pluginDisplayName: string;
  /**
   * `undefined` → plugin-level drill-down (all versions).
   * `null`      → users whose installed version is unknown.
   * `string`    → users on that exact version.
   */
  version: string | null | undefined;
}

/**
 * Server-side DataTable for org-scoped Plugins.
 *
 * The Users column and `userCount`-default sort are gated on owner/manager
 * role — non-admins see the same table without per-plugin user counts.
 */
function DataTableOrgPluginsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement | null {
  const { canViewAdminContent, isLoading: isRoleLoading } =
    useCanViewAdminContent(organizationId);

  const [selectedPluginUsers, setSelectedPluginUsers] =
    useState<SelectedPluginUsers | null>(null);

  useEffect(() => {
    trackEvent('plugins_tab_viewed', {
      organizationId,
      context: 'organization',
    });
  }, [organizationId]);

  const handlePluginUserCountClick = useCallback(
    (plugin: PluginListItem) =>
      setSelectedPluginUsers({
        pluginName: plugin.pluginName,
        marketplace: plugin.marketplace,
        pluginDisplayName: plugin.displayName || plugin.pluginName,
        version: undefined,
      }),
    []
  );

  const handleVersionUserCountClick = useCallback(
    (plugin: PluginListItem, version: string | null) =>
      setSelectedPluginUsers({
        pluginName: plugin.pluginName,
        marketplace: plugin.marketplace,
        pluginDisplayName: plugin.displayName || plugin.pluginName,
        version,
      }),
    []
  );

  const columns = useMemo(
    () =>
      createPluginColumns({
        showUsersColumn: canViewAdminContent,
        onUserCountClick: canViewAdminContent
          ? handlePluginUserCountClick
          : undefined,
        // Org view → wire the dynamic Name/Marketplace/Users filters, the First
        // Seen date filter, and Source sort against the org filter-options
        // endpoint.
        pluginFilters: {
          getQueryKey: (field, params) =>
            queryKeys.plugins.filterOptions(organizationId, field, params),
          queryFn: (field, params) =>
            fetchPluginFieldFilterOptions(organizationId, field, params),
          enableSourceSort: true,
        },
      }),
    [canViewAdminContent, handlePluginUserCountClick, organizationId]
  );

  const initialState = useMemo<Partial<TableQueryState>>(
    () => ({
      sort: canViewAdminContent
        ? { field: 'userCount', order: SortOrder.Desc }
        : { field: 'name', order: SortOrder.Asc },
    }),
    [canViewAdminContent]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) => [
      ...queryKeys.plugins.all,
      'org-data-table',
      organizationId,
      {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      },
    ],
    [organizationId]
  );

  const renderExpandedContent = useCallback(
    (plugin: PluginListItem) => (
      <PluginVersionsTable
        versions={plugin.versions}
        showUsers={canViewAdminContent}
        showFeatures={canViewAdminContent}
        onUserCountClick={
          canViewAdminContent
            ? version => handleVersionUserCountClick(plugin, version)
            : undefined
        }
      />
    ),
    [canViewAdminContent, handleVersionUserCountClick]
  );

  // Wait for role to load — switching the column set (and default sort) mid-flight
  // would clobber any URL-restored state.
  if (isRoleLoading) {
    return null;
  }

  return (
    <>
      <DataTable<PluginListItem>
        // Re-mount when role flips so the new columns + initialState are applied cleanly.
        key={canViewAdminContent ? 'admin' : 'member'}
        id={ORG_PLUGIN_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        initialState={initialState}
        errorBoundaryLabel="Something went wrong in the plugins table"
        search={{
          placeholder: 'Search plugins...',
          className: 'w-[260px]',
        }}
        features={{ enableSorting: true, enableRowExpansion: true }}
        rowExpansion={{
          render: renderExpandedContent,
          getRowId: pluginRowKey,
          expandOnRowClick: true,
          showContainer: false,
          contentClassName: '',
        }}
        queryFn={params => fetchOrgPlugins(organizationId, params)}
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel={
          canViewAdminContent
            ? 'No plugins discovered yet.'
            : 'No plugins installed.'
        }
        countScope={{ organizationId }}
        queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
      />
      <PluginUserListModal
        organizationId={organizationId}
        plugin={
          selectedPluginUsers
            ? {
                pluginName: selectedPluginUsers.pluginName,
                marketplace: selectedPluginUsers.marketplace,
              }
            : null
        }
        pluginDisplayName={selectedPluginUsers?.pluginDisplayName ?? null}
        versionFilter={selectedPluginUsers?.version}
        onClose={() => setSelectedPluginUsers(null)}
      />
    </>
  );
}

DataTableOrgPluginsContent.displayName = 'DataTableOrgPluginsContent';

export default function DataTableOrgPluginsTab({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  return <DataTableOrgPluginsContent organizationId={organizationId} />;
}
